import { type ChoiceCardOption, suggestFishGroupName } from '@/components/organisms/FishGroupForms';

import type { Translate } from './penOptions';
import type { SitePen } from './sitePens';

export interface SplitRowValues {
    name: string;
    /** A select value: the cage id as a string, or '' before one is picked. */
    cageId: string;
    fishCount: string;
}

/**
 * Where a split's new groups can go: the group's own pen (the continuation) and the site's
 * empty pens. A pen another group holds would make it a split-and-merge, which stays internal.
 */
export function splitPenOptions(
    pens: readonly SitePen[],
    currentCageId: number | null,
    t: Translate
): { value: string; label: string }[] {
    return pens
        .filter((pen) => pen.cageId === currentCageId || pen.occupant === null)
        .map((pen) => ({
            value: String(pen.cageId),
            label: t(
                pen.cageId === currentCageId
                    ? 'siteActions.split.penCurrent'
                    : 'siteActions.split.penEmpty',
                { pen: pen.displayName }
            ),
        }));
}

/** A suggested name for a row's pen that no other row, nor any group at the site, has. */
export function suggestRowName(
    pen: SitePen | undefined,
    season: string | null,
    taken: readonly string[]
): string {
    return (
        suggestFishGroupName({
            cageName: pen?.cageName,
            operation: 'split',
            season,
            existingNames: taken,
        }) ?? ''
    );
}

/**
 * Two rows to start from, as the design shows: one staying in the group's pen and one in the
 * first empty pen, each with a suggested name (D6: prefill the convention, always editable).
 */
export function defaultSplitRows(
    pens: readonly SitePen[],
    currentCageId: number | null,
    season: string | null,
    siteNames: readonly string[]
): SplitRowValues[] {
    const current = pens.find((pen) => pen.cageId === currentCageId);
    const empty = pens.find((pen) => pen.occupant === null);
    const rows: SplitRowValues[] = [];
    const taken = [...siteNames];
    for (const pen of [current, empty]) {
        const name = suggestRowName(pen, season, taken);
        if (name) taken.push(name);
        rows.push({ name, cageId: pen ? String(pen.cageId) : '', fishCount: '' });
    }
    return rows;
}

/**
 * The group's bioscopes stay in its pen, or move to one of the rows' other pens. A bioscope
 * already in that pen stays too: a pen can hold several.
 */
export function splitBioscopeOptions(
    pens: readonly SitePen[],
    rowCageIds: readonly number[],
    currentCageId: number | null,
    bioscopeIds: readonly number[],
    t: Translate
): ChoiceCardOption[] {
    const current = pens.find((pen) => pen.cageId === currentCageId);
    const ids = bioscopeIds.join(', ');
    const keep: ChoiceCardOption = {
        value: 'keep',
        label: t('siteActions.split.bioscopeKeep', { ids, pen: current?.displayName ?? '' }),
    };
    const moves = [...new Set(rowCageIds)]
        .filter((cageId) => cageId !== currentCageId)
        .flatMap((cageId) => {
            const pen = pens.find((candidate) => candidate.cageId === cageId);
            if (!pen) return [];
            return [
                {
                    value: String(cageId),
                    label:
                        pen.bioscopeIds.length > 0
                            ? t('siteActions.split.bioscopeMoveTo', {
                                  ids,
                                  pen: pen.displayName,
                                  others: pen.bioscopeIds.join(', '),
                              })
                            : t('siteActions.split.bioscopeMoveToEmpty', {
                                  ids,
                                  pen: pen.displayName,
                              }),
                },
            ];
        });
    return [keep, ...moves];
}
